import { Controller } from "@hotwired/stimulus"

// Drives the onboarding chat: keeps the message list scrolled to the bottom,
// clears the composer after sending, and reloads the checklist frame whenever
// the assistant replies (its tool calls may have completed setup steps).
export default class extends Controller {
  static targets = ["messages", "input", "typing"]
  static values = { checklistFrame: String }

  connect() {
    this.scrollToBottom()
    this.observer = new MutationObserver((mutations) => this.messagesChanged(mutations))
    this.observer.observe(this.messagesTarget, { childList: true, subtree: true })
  }

  disconnect() {
    this.observer?.disconnect()
  }

  afterSubmit(event) {
    if (event.detail.success) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
      this.watchForReply()
    }
  }

  // If Action Cable misses the reply, the typing dots sit forever. Reload
  // once so the saved assistant message appears.
  watchForReply() {
    clearTimeout(this.replyWatch)
    this.replyWatch = setTimeout(() => {
      if (this.hasTypingTarget && this.typingTarget.innerHTML.trim() !== "") {
        window.location.reload()
      }
    }, 20000)
  }

  // Fills the composer with a suggested prompt from the checklist so the
  // user can tweak it (e.g. add their gym's name) before sending.
  usePrompt(event) {
    this.inputTarget.value = event.params.prompt
    this.inputTarget.focus()
  }

  messagesChanged(mutations) {
    this.scrollToBottom()

    const assistantReplied = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) => node.nodeType === Node.ELEMENT_NODE && node.dataset?.role === "assistant"
      )
    )
    if (assistantReplied) {
      clearTimeout(this.replyWatch)
      this.clearTyping()
      this.reloadChecklist()
    }
  }

  // The server also clears the typing indicator, but doing it here too makes
  // the cleanup safe against broadcast/response ordering races.
  clearTyping() {
    if (this.hasTypingTarget) this.typingTarget.innerHTML = ""
  }

  reloadChecklist() {
    const frame = document.getElementById(this.checklistFrameValue)
    if (!frame) return

    if (frame.src) {
      frame.reload()
    } else {
      frame.src = frame.dataset.src
    }
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }
}
